// ============================================
// Creator Module - Statistics (ES6)
// ============================================

import { escapeHtml } from '../utils.js';
import {
  getCurrentCreations,
  getCurrentStatsCreationId, setCurrentStatsCreationId,
  getCurrentStatsPeriod, setCurrentStatsPeriod
} from './state.js';
import { getCurrentUser } from '../app/state.js';
import { authFetch } from '../shared/auth-fetch.js';
import { CREATOR_ENDPOINTS } from '../shared/api-endpoints.js';

// ============================================
// 통계 모달
// ============================================

export async function openStats(creationId) {
  setCurrentStatsCreationId(creationId);
  setCurrentStatsPeriod(7);

  const modal = document.getElementById('creatorStatsModal');
  if (!modal) return;

  // 슬라이드 애니메이션으로 열기
  modal.classList.add('open');

  // ModalManager에 push
  if (window.ModalManager) {
    window.ModalManager.push('creatorStatsModal');
  }

  await loadAndRenderStats();
}

export function closeStats() {
  const modal = document.getElementById('creatorStatsModal');
  if (modal) {
    modal.classList.remove('open');
  }
  setCurrentStatsCreationId(null);

  // ModalManager에서 제거
  if (window.ModalManager) {
    window.ModalManager.remove('creatorStatsModal', true);
  }
}

export async function loadAndRenderStats() {
  const statsCreationId = getCurrentStatsCreationId();
  if (!statsCreationId || !getCurrentUser()) return;

  const container = document.getElementById('statsContent');
  if (!container) return;

  container.innerHTML = '<div class="stats-loading"><div class="spinner"></div></div>';

  try {
    const response = await authFetch(CREATOR_ENDPOINTS.loadCreationStats, {
      body: {
        creationId: statsCreationId,
        days: getCurrentStatsPeriod()
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      container.innerHTML = `<div class="stats-error">${errorData.error || '통계를 불러오는데 실패했습니다'}</div>`;
      return;
    }

    const result = await response.json();
    if (!result.success || !result.stats) {
      container.innerHTML = '<div class="stats-error">통계 데이터가 없습니다</div>';
      return;
    }

    renderStats(result.stats);
  } catch (error) {
    console.error('통계 로드 오류:', error);
    container.innerHTML = '<div class="stats-error">통계를 불러오는데 실패했습니다</div>';
  }
}

function renderStats(stats) {
  const container = document.getElementById('statsContent');
  if (!container) return;

  const statsCreationId = getCurrentStatsCreationId();
  const creation = getCurrentCreations().find(c => c.id === statsCreationId);

  // 전체 기간 대화 수 (표시 및 뱃지 조건용)
  const allTimePlays = stats.overview.chatCount || 0;
  const showBadges = allTimePlays >= 50;

  // 성과 기준 임계값
  const thresholds = {
    ctr: { good: 5, bad: 1 },
    conversionRate: { good: 10, bad: 3 },
    retention10Rate: { good: 30, bad: 10 }
  };

  // 바 색상 클래스 판정 함수
  const getBarColorClass = (metric, value) => {
    const t = thresholds[metric];
    if (!t) return '';
    const v = parseFloat(value);
    if (v >= t.good) return 'bar-good';
    if (v <= t.bad) return 'bar-bad';
    return 'bar-normal';
  };

  // 뱃지 판정 함수
  const getBadge = (metric, value) => {
    if (!showBadges) return '';

    const t = thresholds[metric];
    if (!t) return '';

    const v = parseFloat(value);
    if (v >= t.good) {
      return '<span class="stats-badge good">좋음</span>';
    } else if (v <= t.bad) {
      return '<span class="stats-badge bad">나쁨</span>';
    } else {
      return '<span class="stats-badge normal">보통</span>';
    }
  };

  // 작품 정보 가져오기
  const creationName = creation?.characterName || creation?.title || '작품';
  const creationDesc = creation?.shortDesc || creation?.shortIntro || '';
  const creationImage = creation?.imageUrl || '';

  container.innerHTML = `
    <!-- 작품 정보 섹션 -->
    <div class="stats-creation-info">
      <div class="stats-creation-image">
        ${creationImage ? `<img src="${creationImage}" alt="${escapeHtml(creationName)}">` : `
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
            <circle cx="8.5" cy="8.5" r="1.5"/>
            <polyline points="21 15 16 10 5 21"/>
          </svg>
        `}
      </div>
      <div class="stats-creation-details">
        <h3 class="stats-creation-name">${escapeHtml(creationName)}</h3>
        ${creationDesc ? `<p class="stats-creation-desc">${escapeHtml(creationDesc)}</p>` : ''}
        <div class="stats-creation-dates">
          <span>출시 ${formatDate(stats.overview.publishedAt) || '-'}</span>
          <span>수정 ${formatDate(stats.overview.updatedAt) || '-'}</span>
        </div>
      </div>
    </div>

    <!-- 편집 버튼 -->
    <div class="stats-actions">
      <button class="stats-edit-btn" data-action="openEditor">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
        </svg>
        작품 편집하기
      </button>
    </div>

    <!-- 구분선 -->
    <div class="stats-divider"></div>

    <!-- 핵심 지표 -->
    <div class="stats-summary-row">
      <div class="stats-summary-item">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
        </svg>
        <span class="stats-summary-value">${formatNumber(allTimePlays)}</span>
        <span class="stats-summary-label">대화</span>
      </div>
      <div class="stats-summary-item accent">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
        </svg>
        <span class="stats-summary-value">${formatNumber(stats.overview.likeCount || 0)}</span>
        <span class="stats-summary-label">좋아요</span>
      </div>
    </div>

    <!-- 대화량 차트 -->
    <div class="stats-section">
      <div class="stats-section-header">
        <h4>대화량 추이</h4>
        <button class="stats-period-filter" data-action="togglePeriodMenu">
          ${getCurrentStatsPeriod()}일
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </button>
      </div>
      <div class="stats-chart-card">
        ${renderChart(stats.daily)}
      </div>
    </div>

    <!-- 성과 지표 -->
    <div class="stats-section">
      <div class="stats-section-header">
        <h4>성과 지표</h4>
        ${!showBadges ? '<span class="stats-section-sub">50회 이상 대화 시 평가 표시</span>' : ''}
      </div>
      <div class="stats-metrics-dashboard">
        <div class="stats-metric-card" data-action="showMetricInfo" data-metric="impressions">
          <div class="stats-metric-header">
            <span class="stats-metric-title">노출 수</span>
            <svg class="stats-metric-info-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>
            </svg>
          </div>
          <div class="stats-metric-body">
            <span class="stats-metric-value">${formatNumber(stats.metrics.impressions)}</span>
          </div>
        </div>
        <div class="stats-metric-card" data-action="showMetricInfo" data-metric="ctr">
          <div class="stats-metric-header">
            <span class="stats-metric-title">클릭률</span>
            ${getBadge('ctr', stats.metrics.ctr)}
            <svg class="stats-metric-info-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>
            </svg>
          </div>
          <div class="stats-metric-body">
            <span class="stats-metric-value">${stats.metrics.ctr}%</span>
          </div>
          <div class="stats-metric-bar">
            <div class="stats-metric-bar-fill ${getBarColorClass('ctr', stats.metrics.ctr)}" style="width: ${Math.min(parseFloat(stats.metrics.ctr), 100)}%"></div>
          </div>
        </div>
        <div class="stats-metric-card" data-action="showMetricInfo" data-metric="conversionRate">
          <div class="stats-metric-header">
            <span class="stats-metric-title">대화 전환율</span>
            ${getBadge('conversionRate', stats.metrics.conversionRate)}
            <svg class="stats-metric-info-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>
            </svg>
          </div>
          <div class="stats-metric-body">
            <span class="stats-metric-value">${stats.metrics.conversionRate}%</span>
          </div>
          <div class="stats-metric-bar">
            <div class="stats-metric-bar-fill ${getBarColorClass('conversionRate', stats.metrics.conversionRate)}" style="width: ${Math.min(parseFloat(stats.metrics.conversionRate), 100)}%"></div>
          </div>
        </div>
        <div class="stats-metric-card" data-action="showMetricInfo" data-metric="retention10Rate">
          <div class="stats-metric-header">
            <span class="stats-metric-title">10회 이상 대화율</span>
            ${getBadge('retention10Rate', stats.metrics.retention10Rate)}
            <svg class="stats-metric-info-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>
            </svg>
          </div>
          <div class="stats-metric-body">
            <span class="stats-metric-value">${stats.metrics.retention10Rate}%</span>
          </div>
          <div class="stats-metric-bar">
            <div class="stats-metric-bar-fill ${getBarColorClass('retention10Rate', stats.metrics.retention10Rate)}" style="width: ${Math.min(parseFloat(stats.metrics.retention10Rate), 100)}%"></div>
          </div>
        </div>
      </div>
    </div>
  `;

  // 이벤트 위임 설정
  container.onclick = (e) => {
    const actionEl = e.target.closest('[data-action]');
    if (!actionEl) return;

    const action = actionEl.dataset.action;
    if (action === 'openEditor') {
      import('./editor.js').then(({ openEditor }) => {
        openEditor(getCurrentStatsCreationId());
      });
    } else if (action === 'togglePeriodMenu') {
      togglePeriodMenu(actionEl);  // 버튼 요소 직접 전달
    } else if (action === 'showMetricInfo') {
      showMetricInfo(actionEl.dataset.metric);
    }
  };
}

function renderChart(dailyData) {
  if (!dailyData || dailyData.length === 0) {
    return '<div class="stats-chart-empty">데이터가 없습니다</div>';
  }

  // 일별 메시지 수 기준으로 차트 렌더링
  const maxMessages = Math.max(...dailyData.map(d => d.messages || 0), 1);
  const totalDays = dailyData.length;

  // 30일일 때는 5일 간격으로 레이블 표시
  const labelInterval = totalDays > 14 ? 5 : 1;

  return `
    <div class="stats-chart-bars">
      ${dailyData.map((d, idx) => {
        const msgCount = d.messages || 0;
        const hasValue = msgCount > 0;
        const height = hasValue ? Math.max((msgCount / maxMessages) * 100, 4) : 0;
        const date = new Date(d.date);
        const label = `${date.getMonth() + 1}/${date.getDate()}`;

        // 레이블 표시 여부
        const showLabel = idx === totalDays - 1 || idx % labelInterval === 0;

        return `
          <div class="stats-chart-bar-container">
            ${hasValue ? `
              <div class="stats-chart-bar" style="height: ${height}%">
                ${msgCount > 0 ? `<span class="stats-chart-bar-value">${msgCount}</span>` : ''}
              </div>
            ` : `
              <div class="stats-chart-bar-empty"></div>
            `}
            <span class="stats-chart-bar-label ${showLabel ? '' : 'hidden'}">${label}</span>
          </div>
        `;
      }).join('')}
    </div>
  `;
}

// ============================================
// 기간 변경
// ============================================

export async function changePeriod(days) {
  setCurrentStatsPeriod(days);
  // 기간 선택 메뉴 닫기
  const existingMenu = document.querySelector('.stats-period-menu');
  if (existingMenu) existingMenu.remove();
  await loadAndRenderStats();
}

export function togglePeriodMenu(btn) {
  // 기존 메뉴 제거
  const existingMenu = document.querySelector('.stats-period-menu');
  if (existingMenu) {
    existingMenu.remove();
    return;
  }

  const rect = btn.getBoundingClientRect();

  const menu = document.createElement('div');
  menu.className = 'stats-period-menu';
  const statsPeriod = getCurrentStatsPeriod();
  menu.innerHTML = `
    <button class="${statsPeriod === 7 ? 'active' : ''}" data-action="changePeriod" data-period="7">7일</button>
    <button class="${statsPeriod === 30 ? 'active' : ''}" data-action="changePeriod" data-period="30">30일</button>
  `;

  // 이벤트 위임 설정
  menu.addEventListener('click', (e) => {
    const actionEl = e.target.closest('[data-action]');
    if (actionEl && actionEl.dataset.action === 'changePeriod') {
      changePeriod(parseInt(actionEl.dataset.period, 10));
      menu.remove();
    }
  });

  // 버튼 아래에 위치
  menu.style.position = 'fixed';
  menu.style.top = `${rect.bottom + 4}px`;
  menu.style.right = `${window.innerWidth - rect.right}px`;

  document.body.appendChild(menu);

  // 외부 클릭 시 닫기
  setTimeout(() => {
    document.addEventListener('click', function closeMenu(e) {
      if (!menu.contains(e.target)) {
        menu.remove();
        document.removeEventListener('click', closeMenu);
      }
    });
  }, 0);
}

// ============================================
// 지표 설명
// ============================================

const metricInfoData = {
  impressions: {
    title: '노출 수',
    description: '작품이 홈 화면이나 검색 결과에 표시된 횟수입니다.',
    tips: [
      '노출 수는 작품이 사용자에게 보여진 총 횟수를 의미합니다.',
      '높은 노출 수는 작품이 많은 사용자에게 도달했음을 나타냅니다.',
      '태그와 설명을 최적화하면 노출 수를 높일 수 있습니다.'
    ]
  },
  ctr: {
    title: '클릭률 (CTR)',
    description: '작품을 본 사용자 중 실제로 클릭한 비율입니다.',
    formula: '클릭 수 ÷ 노출 수 × 100',
    tips: [
      '높은 클릭률은 썸네일과 제목이 매력적임을 의미합니다.',
      '일반적으로 5% 이상이면 좋은 성과입니다.',
      '10% 이상은 매우 우수한 클릭률입니다.'
    ]
  },
  conversionRate: {
    title: '대화 전환율',
    description: '작품을 클릭한 사용자 중 실제로 대화를 시작한 비율입니다.',
    formula: '대화 시작 수 ÷ 클릭 수 × 100',
    tips: [
      '높은 전환율은 작품 설명과 첫인상이 좋음을 의미합니다.',
      '10% 이상이면 좋은 전환율입니다.',
      '20% 이상은 매우 우수한 전환율입니다.'
    ]
  },
  retention10Rate: {
    title: '10회 이상 대화율',
    description: '대화를 시작한 사용자 중 10회 이상 대화를 이어간 비율입니다.',
    formula: '10회 이상 대화 수 ÷ 총 대화 수 × 100',
    tips: [
      '이 지표는 작품의 몰입도와 재미를 나타냅니다.',
      '높을수록 사용자가 캐릭터와 오래 대화했음을 의미합니다.',
      '캐릭터의 개성과 스토리가 이 지표에 영향을 줍니다.'
    ]
  }
};

export function showMetricInfo(metricKey) {
  const info = metricInfoData[metricKey];
  if (!info) return;

  // 기존 모달 제거
  const existingModal = document.querySelector('.stats-metric-info-modal');
  if (existingModal) existingModal.remove();

  const modal = document.createElement('div');
  modal.className = 'stats-metric-info-modal';
  modal.innerHTML = `
    <div class="stats-metric-info-overlay" data-action="closeMetricInfo"></div>
    <div class="stats-metric-info-sheet">
      <div class="stats-metric-info-header">
        <h3>${info.title}</h3>
        <button class="stats-metric-info-close" data-action="closeMetricInfo">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>
      <div class="stats-metric-info-body">
        <p class="stats-metric-info-desc">${info.description}</p>
        ${info.formula ? `
          <div class="stats-metric-info-formula">
            <span class="formula-label">계산 방법</span>
            <span class="formula-value">${info.formula}</span>
          </div>
        ` : ''}
        <div class="stats-metric-info-tips">
          <span class="tips-label">팁</span>
          <ul>
            ${info.tips.map(tip => `<li>${tip}</li>`).join('')}
          </ul>
        </div>
      </div>
    </div>
  `;

  // 이벤트 위임 설정
  modal.addEventListener('click', (e) => {
    const actionEl = e.target.closest('[data-action]');
    if (actionEl && actionEl.dataset.action === 'closeMetricInfo') {
      closeMetricInfo();
    }
  });

  document.body.appendChild(modal);

  // 애니메이션
  requestAnimationFrame(() => {
    modal.classList.add('active');
  });
}

export function closeMetricInfo() {
  const modal = document.querySelector('.stats-metric-info-modal');
  if (modal) {
    modal.classList.remove('active');
    setTimeout(() => modal.remove(), 300);
  }
}

// ============================================
// 유틸리티
// ============================================

function formatNumber(num) {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num.toString();
}

function formatDate(dateValue) {
  if (!dateValue) return null;
  const date = new Date(dateValue);
  if (isNaN(date.getTime())) return null;
  return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`;
}
